/**
 * React Hook & Context for AIShield Customer Authentication (Stage 6A)
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import {
  authManager,
  AuthManager,
  type AuthState,
} from "../services/authManager.ts";
import type {
  AccountResponse,
  LoginPayload,
  RegisterPayload,
} from "../services/apiClient.ts";

export interface AuthContextValue extends AuthState {
  login: (payload: LoginPayload) => Promise<AccountResponse>;
  register: (payload: RegisterPayload) => Promise<AccountResponse>;
  logout: () => Promise<void>;
  checkSession: () => Promise<AuthState>;
  getSessionSignal: () => AbortSignal;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: React.ReactNode;
  manager?: AuthManager;
  autoCheck?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  manager = authManager,
  autoCheck = true,
}) => {
  const [state, setState] = useState<AuthState>(() => manager.getState());

  useEffect(() => {
    const unsubscribe = manager.subscribe(setState);
    if (autoCheck) {
      manager.checkSession();
    }
    return () => {
      unsubscribe();
    };
  }, [manager, autoCheck]);

  const login = useCallback(
    async (payload: LoginPayload) => {
      return await manager.login(payload);
    },
    [manager]
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      return await manager.register(payload);
    },
    [manager]
  );

  const logout = useCallback(async () => {
    await manager.logout();
  }, [manager]);

  const checkSession = useCallback(async () => {
    return await manager.checkSession();
  }, [manager]);

  const getSessionSignal = useCallback(() => {
    return manager.getSessionSignal();
  }, [manager]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      register,
      logout,
      checkSession,
      getSessionSignal,
    }),
    [state, login, register, logout, checkSession, getSessionSignal]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
